import type { EmblaCarouselType } from 'embla-carousel';
import { Box, IconButton, Link, Text } from '@ksyos/design-system';
import useEmblaCarousel from 'embla-carousel-react';
import { chunk } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { css, styled } from 'styled-components';
import type { IEpisodeCategory } from '@common/interfaces';
import { Breakpoints, EpisodeCategory } from '@common/enums';
import { useViewportWidth } from '@frontend/utils';
import { CarePathInformationRequestModal } from './CarePathInformationRequestModal';

export interface CarouselProps {
    recommendations: IEpisodeCategory[];
}

export function Carousel({ recommendations }: CarouselProps) {
    const { width } = useViewportWidth();
    const [selectedCarePath, setSelectedCarePath] = useState<EpisodeCategory | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    function getItemsPerSlide() {
        if (width < Breakpoints.medium) {
            return 1;
        }
        if (width > Breakpoints.medium && width < Breakpoints.xlarge) {
            return 2;
        }

        return 3;
    }

    const itemsPerSlide = getItemsPerSlide();

    const slides = chunk(recommendations, itemsPerSlide);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        loop: false,
    });

    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    const scrollPrev = useCallback(() => {
        if (emblaApi) {
            emblaApi.scrollPrev();
        }
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) {
            emblaApi.scrollNext();
        }
    }, [emblaApi]);

    const scrollTo = useCallback(
        (index: number) => {
            if (emblaApi) {
                emblaApi.scrollTo(index);
            }
        },
        [emblaApi]
    );

    useEffect(() => {
        if (!emblaApi) {
            return;
        }

        onSelect(emblaApi);
        setScrollSnaps(emblaApi.scrollSnapList());
        emblaApi.on('reInit', onSelect);
        emblaApi.on('select', onSelect);

        return () => {
            emblaApi.off('reInit', onSelect);
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    useEffect(() => {
        if (emblaApi) {
            emblaApi.reInit();
            setScrollSnaps(emblaApi.scrollSnapList());
        }
    }, [emblaApi, itemsPerSlide]);

    const canScrollPrev = emblaApi ? emblaApi.canScrollPrev() : false;
    const canScrollNext = emblaApi ? emblaApi.canScrollNext() : false;

    return (
        <div>
            <IconButton
                disabled={!canScrollPrev}
                icon="caret-left"
                labelText="Previous Item"
                onClick={scrollPrev}
                sx={{
                    ':hover': { opacity: 1 },
                    left: 2,
                    opacity: !canScrollPrev ? 0 : 0.7,
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                }}
                variant="filled"
            />

            <StyledEmblaViewport ref={emblaRef}>
                <StyledEmblaContainer>
                    {slides.map((slide, index) => (
                        <StyledEmblaSlide key={index}>
                            <CarouselSlide>
                                {slide.map((recommendation: IEpisodeCategory) => (
                                    <CarouselItem
                                        key={recommendation.id}
                                        recommendation={recommendation}
                                        setSelectedCarePath={setSelectedCarePath}
                                    />
                                ))}
                            </CarouselSlide>
                        </StyledEmblaSlide>
                    ))}
                </StyledEmblaContainer>
            </StyledEmblaViewport>

            <IconButton
                disabled={!canScrollNext}
                icon="caret-right"
                labelText="Next Item"
                onClick={scrollNext}
                sx={{
                    ':hover': { opacity: 1 },
                    opacity: !canScrollNext ? 0 : 0.7,
                    position: 'absolute',
                    right: 2,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                }}
                variant="filled"
            />

            <Box
                alignItems="center"
                display="flex"
                gap={2}
                justifyContent="center"
                pt={6}
            >
                {scrollSnaps.length > 1 &&
                    scrollSnaps.map((_, index) => (
                        <StyledDot
                            $isActive={selectedIndex === index}
                            key={index}
                            onClick={() => scrollTo(index)}
                        />
                    ))}
            </Box>
            {selectedCarePath && (
                <CarePathInformationRequestModal
                    onClose={() => setSelectedCarePath(null)}
                    selectedCarePath={selectedCarePath}
                />
            )}
        </div>
    );
}

function CarouselSlide({ children }: { children: React.ReactNode }) {
    return (
        <Box
            display="grid"
            gap={16}
            gridTemplateColumns={[
                'minmax(220px, 1fr)',
                null,
                'repeat(2,minmax(220px, 1fr))',
                null,
                'repeat(3,minmax(220px, 1fr))',
            ]}
            sx={{
                ':active': {
                    cursor: 'grabbing',
                },
                cursor: 'grab',
            }}
            width="100%"
        >
            {children}
        </Box>
    );
}

function CarouselItem({
    recommendation,
    setSelectedCarePath,
}: {
    recommendation: IEpisodeCategory;
    setSelectedCarePath: (name: EpisodeCategory) => void;
}) {
    const { image, title, content, action } = getCarouselItemContent(recommendation.name);

    return (
        <StyledCarouselItem
            key={recommendation.id}
            position="relative"
        >
            <Box
                display="flex"
                gap={6}
                sx={{ userSelect: 'none' }}
            >
                <img
                    alt={title}
                    draggable="false"
                    height="100px"
                    src={image}
                    style={{ flexShrink: '0' }}
                    width="100px"
                />
                <Box>
                    <Text
                        as="h3"
                        fontWeight="medium"
                        mb={2}
                        mt={3}
                        size="lg"
                    >
                        {title}
                    </Text>
                    <Text size="sm">{content}</Text>
                </Box>
            </Box>
            <Text
                mt={6}
                size="md"
            >
                <Link
                    color="subtle"
                    onClick={() => setSelectedCarePath(recommendation.name)}
                    variant="button"
                >
                    {action}
                </Link>
            </Text>
        </StyledCarouselItem>
    );
}

function getCarouselItemContent(episodeCategory: EpisodeCategory) {
    const mapping = {
        [EpisodeCategory.somnology]: {
            action: 'Ontvang meer info over Slaap',
            content: 'Verdenk jij een patiënt van slaapapneu?',
            image: '/assets/images/recommendations/icons/somnology.svg',
            title: 'Slaap',
        },
        [EpisodeCategory.cardiology]: {
            action: 'Ontvang meer info over Cardiologie',
            content: 'Zie jij signalen van hartproblemen bij een patiënt?',
            image: '/assets/images/recommendations/icons/cardiology.svg',
            title: 'Cardiologie',
        },
        [EpisodeCategory.ophthalmology]: {
            action: 'Ontvang meer info over Oogheelkunde',
            content: 'Wil je dat een patiënt snel gezien wordt?',
            image: '/assets/images/recommendations/icons/ophthalmology.svg',
            title: 'Oogheelkunde',
        },
        [EpisodeCategory.mentalHealth]: {
            action: 'Ontvang meer info over GGZ',
            content: 'Wil jij een patiënt kunnen helpen, ondanks de wachttijden?',
            image: '/assets/images/recommendations/icons/mental-health.svg',
            title: 'GGZ',
        },
        [EpisodeCategory.dermatology]: {
            action: 'Ontvang meer info over Dermatologie',
            content: 'Twijfel je over een verdacht huidplekje?',
            image: '/assets/images/recommendations/icons/dermatology.svg',
            title: 'Dermatologie',
        },
    };

    return mapping[episodeCategory as keyof typeof mapping];
}

const StyledEmblaViewport = styled.div`
    overflow: hidden;
`;

const StyledEmblaContainer = styled.div`
    display: flex;
`;

const StyledEmblaSlide = styled.div`
    flex: 0 0 100%;
    min-width: 0;
`;

const StyledCarouselItem = styled(Box)`
    ${(props) => props.theme.mediaQueries.medium} {
        & + &::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: -32px;
            height: 100%;
            border: none;
            width: 1px;
            background-color: ${(props) => props.theme.colors.neutral300};
        }
    }
`;

const StyledDot = styled.div<{ $isActive: boolean }>`
    border-radius: 50%;
    height: 8px;
    width: 8px;
    background-color: ${(props) => (props.$isActive ? props.theme.colors.neutral800 : props.theme.colors.neutral300)};
    transition:
        transform 0.3s ease-in-out,
        background-color 0.3s ease-in-out;
    cursor: pointer;
    ${(props) =>
        props.$isActive &&
        css`
            transform: scale(1.25);
        `};
`;
